import { Button } from "@/components/ui/button";
import { HardDriveDownload } from "lucide-react";
import { useState, useMemo } from "react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { pwaStatus, promptInstall } from "@/features/pwa-controller";
import { useSignals } from "@preact/signals-react/runtime";
import { useTranslation } from "@/providers/language-provider";

const localeText = {
  en: {
    install_app: "Install App",
    install_desc:
      "For optimal user experience and full functionality, please install the application. Installation allows seamless access to all features, including offline access and notifications.",
    install_aleady:
      "If you have already installed it, please launch the installed app.",
    install_manual: "Manual Installation",
    install_chrome:
      "1. Click the ⋮ (menu) on the right side of the address bar\n2. Select 'Install app' or 'Add to Home screen'",
    install_safari:
      "1. Click the share button (□↑) at the bottom\n2. Select 'Add to Home Screen'",
    install_edge:
      "1. Click the ... (menu) on the right side of the address bar\n2. Select 'Apps' → 'Install this site as an app'",
    button_install: "Install",
    button_cancel: "Cancel",
    button_installing: "Installing...",
  },
  ko: {
    install_app: "앱 설치",
    install_desc:
      "최적의 사용자 경험과 기능 활용을 위해 애플리케이션을 설치하시기 바랍니다. 설치하면 오프라인 접근과 알림 기능을 포함한 모든 기능을 원활하게 이용하실 수 있습니다.",
    install_aleady: "이미 설치된 경우 설치된 앱을 실행해 주세요.",
    install_manual: "수동 설치 방법",
    install_chrome:
      "1. 주소창 우측의 ⋮ (메뉴) 클릭\n2. '앱 설치' 또는 '홈 화면에 추가' 선택",
    install_safari: "1. 하단의 공유 버튼 (□↑) 클릭\n2. '홈 화면에 추가' 선택",
    install_edge:
      "1. 주소창 우측의 ... (메뉴) 클릭\n2. '앱' → '이 사이트를 앱으로 설치' 선택",
    button_install: "설치",
    button_cancel: "취소",
    button_installing: "설치 중...",
  },
} as const;

export function InstallWidget() {
  useSignals();
  const { language } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);
  const [isInstalling, setIsInstalling] = useState(false);

  const text = useMemo(() => {
    return localeText[language as keyof typeof localeText] ?? localeText["en"];
  }, [language]);

  // 독립 실행 모드로 실행 중이면 위젯 숨김
  if (pwaStatus.value.isStandaloneMode) {
    return null;
  }

  const handleInstall = async () => {
    setIsInstalling(true);
    try {
      const result = await promptInstall();
      if (result) {
        setIsOpen(false);
      }
    } catch (error) {
      console.error("설치 중 오류:", error);
    } finally {
      setIsInstalling(false);
    }
  };

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <div className="relative">
          <Button variant="destructive" size="icon">
            <HardDriveDownload className="h-4 w-4" />
          </Button>
          <span className="absolute top-[-2px] right-[-2px] flex h-3 w-3">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-90"></span>
            <span className="relative inline-flex rounded-full h-3 w-3 bg-red-600"></span>
          </span>
        </div>
      </PopoverTrigger>
      <PopoverContent className="w-80">
        <div className="space-y-4">
          <div className="space-y-2">
            <h4 className="font-medium leading-none">{text.install_app}</h4>
            <p className="text-sm text-muted-foreground">{text.install_desc}</p>
            <p className="text-sm text-foreground">{text.install_aleady}</p>
          </div>

          <div className="flex gap-2 justify-end">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setIsOpen(false)}
            >
              {text.button_cancel}
            </Button>
            <Button
              size="sm"
              onClick={handleInstall}
              disabled={isInstalling || !pwaStatus.value.canInstall}
              className="gap-2"
            >
              <HardDriveDownload className="h-4 w-4" />
              {isInstalling ? text.button_installing : text.button_install}
            </Button>
          </div>

          {/* 수동 설치 안내 */}
          <div className="border-t pt-4 space-y-2">
            <h5 className="text-sm font-medium">{text.install_manual}</h5>
            <div className="text-xs text-muted-foreground space-y-2">
              <div>
                <strong>Chrome (Android/PC):</strong>
                <div className="ml-2 mt-1 whitespace-pre-line">
                  {text.install_chrome}
                </div>
              </div>
              <div>
                <strong>Safari (iOS):</strong>
                <div className="ml-2 mt-1 whitespace-pre-line">
                  {text.install_safari}
                </div>
              </div>
              <div>
                <strong>Edge:</strong>
                <div className="ml-2 mt-1 whitespace-pre-line">
                  {text.install_edge}
                </div>
              </div>
            </div>
          </div>
        </div>
      </PopoverContent>
    </Popover>
  );
}
